import {
  CleaningZone,
  CleaningZoneOrderingRequest,
  CleaningZoneRead,
  MainService,
  Mission,
  MissionPointTransferRouteRead,
  MissionRead,
  MissionReadList,
  MissionStatus,
  PatchedMissionRequest,
  RestrictZone,
  TransferRouteRead,
} from 'api/schema/index'
import { LineString, Polygon } from 'geojson'
import chunk from 'lodash.chunk'
import {
  DEFAULT_VIZUALIZATION_SETTINGS,
  MISSION_CALCULATING_STATUSES,
  MISSION_ZONE_TYPES,
  SUB_CLEAN_MODES,
  TRANSFER_CALCULATING_STATUSES,
  TRANSFER_TYPE,
} from 'utils/constants'
import {
  ICleaningModeConfig,
  ICleaningZone,
  ICleaningZonePoint,
  IManualCleaningParams,
  IMission,
  IMissionInfo,
  IPoint,
  IRestrictZone,
  IRoute,
  ITransferRoute,
  IVisualizationSettings,
  IZone,
  IZoneTransferRoute,
  PropsPoints,
  ZoneCreateData,
  ZoneUpdateData,
} from 'utils/interfaces'
import { toApriorMapVersionId, toMissionId } from 'utils/types'

const normalizeCleaningConfig = (config: CleaningZoneRead['cleaning_config']): ICleaningModeConfig | undefined => {
  if (!config) return undefined
  if (config.mode === 'Automatic') {
    return { mode: SUB_CLEAN_MODES.AUTO, autoMode: config.autoMode ?? 1 }
  }
  if (config.mode === 'Manually' && config.params) {
    return { mode: SUB_CLEAN_MODES.MANUAL, params: config.params as IManualCleaningParams }
  }
  return undefined
}

const toDtoCleaningConfig = (config: ICleaningModeConfig) => {
  if (config.mode === SUB_CLEAN_MODES.AUTO) {
    return { mode: 'Automatic' as const, autoMode: config.autoMode }
  }
  if (config.mode === SUB_CLEAN_MODES.MANUAL) {
    return { mode: 'Manually' as const, params: config.params }
  }
  return null
}

const normalizeCleaningZone = (zoneRaw: CleaningZoneRead | CleaningZone): ICleaningZone => {
  const cleaningConfig = normalizeCleaningConfig(zoneRaw.cleaning_config ?? null)

  return {
    id: 'cleaning_' + zoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: (zoneRaw.geometry as Polygon).coordinates,
    },
    properties: {
      zone_type: MISSION_ZONE_TYPES.CLEANING,
      name: zoneRaw.name || '',
      description: zoneRaw.description || '',
      zone_id: zoneRaw.id,
      id_on_map: 'cleaning_' + zoneRaw.id,
      sorting: zoneRaw.sorting,
      status: (zoneRaw as CleaningZoneRead).status
        ? ((zoneRaw as CleaningZoneRead).status as MISSION_CALCULATING_STATUSES)
        : MISSION_CALCULATING_STATUSES.NEW,
      readyForCalculation: (zoneRaw as CleaningZoneRead).is_ready_to_start_calculation
        ? (zoneRaw as CleaningZoneRead).is_ready_to_start_calculation
        : true,
      length: (zoneRaw as CleaningZoneRead).cleaning_route_geometry_length || undefined,
      area: (zoneRaw as CleaningZoneRead).geometry_area || undefined,
      executionTime: (zoneRaw as CleaningZoneRead).cleaning_route_execution_time || undefined,
      cleaningConfig,
    },
  }
}

const normalizeRestrictZone = (zoneRaw: RestrictZone): IRestrictZone => {
  return {
    id: 'restrict_' + zoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: (zoneRaw.geometry as Polygon).coordinates,
    },
    properties: {
      zone_type: MISSION_ZONE_TYPES.RESTRICT,
      name: zoneRaw.name || '',
      description: zoneRaw.description || '',
      zone_id: zoneRaw.id,
      id_on_map: 'restrict_' + zoneRaw.id,
    },
  }
}

const normalizeCleaningRoute = (cleaningZoneRaw: CleaningZoneRead): IRoute => {
  return {
    id: 'cleaning_route_' + cleaningZoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'MultiLineString',
      coordinates: cleaningZoneRaw.cleaning_route_geometry?.coordinates
        ? chunk(cleaningZoneRaw.cleaning_route_geometry.coordinates[0], 30000)
        : [],
    },
    properties: {
      zone_id: cleaningZoneRaw.id,
      id_on_map: 'cleaning_' + cleaningZoneRaw.id,
    },
  }
}

const normalizeTransferRoute = (transferRouteRaw: TransferRouteRead): IZoneTransferRoute => {
  return {
    id: 'transfer_route_' + transferRouteRaw.id,
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.geometry?.coordinates || [],
    },
    userGeometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.user_geometry?.coordinates || transferRouteRaw.geometry?.coordinates || [],
    },
    properties: {
      id: transferRouteRaw.id,
      zone_id_from: transferRouteRaw.cleaning_zone_from_id,
      zone_id_to: transferRouteRaw.cleaning_zone_to_id,
      id_on_map: 'transfer_route_' + transferRouteRaw.id,
      type: TRANSFER_TYPE.MIDDLE,
      status: transferRouteRaw.status
        ? (transferRouteRaw.status as TRANSFER_CALCULATING_STATUSES)
        : TRANSFER_CALCULATING_STATUSES.NEW,
      length: transferRouteRaw.geometry_length || transferRouteRaw.user_geometry_length || undefined,
    },
  }
}

const normalizeRoute = (transferRouteRaw: MissionPointTransferRouteRead): ITransferRoute => {
  return {
    id: 'mission_route_' + transferRouteRaw.id,
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.geometry?.coordinates || transferRouteRaw.user_geometry?.coordinates || [],
    },
    userGeometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.user_geometry?.coordinates || transferRouteRaw.geometry?.coordinates || [],
    },
    properties: {
      id: transferRouteRaw.id,
      type: transferRouteRaw.point_type === 'start' ? TRANSFER_TYPE.START : TRANSFER_TYPE.FINISH,
      status: transferRouteRaw.status
        ? (transferRouteRaw.status as TRANSFER_CALCULATING_STATUSES)
        : TRANSFER_CALCULATING_STATUSES.NEW,
      length: transferRouteRaw.geometry_length || transferRouteRaw.user_geometry_length || undefined,
    },
  }
}

const normalizeCleaningZoneStartingPoints = (cleaningZonesRaw: CleaningZoneRead[]): ICleaningZonePoint[] => {
  return cleaningZonesRaw
    .map(cleaningZone => normalizeCleaningZoneStartingPoint(cleaningZone))
    .filter(Boolean) as ICleaningZonePoint[]
}

const normalizeCleaningZoneStartingPoint = (
  cleaningZoneRaw: CleaningZoneRead | CleaningZone
): ICleaningZonePoint | undefined => {
  if (cleaningZoneRaw.starting_point?.coordinates) {
    return {
      id: 'cleaning_' + cleaningZoneRaw.id + '_starting_point',
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: cleaningZoneRaw.starting_point?.coordinates,
      },
      properties: {
        zone_id: cleaningZoneRaw.id,
        id_on_map: 'cleaning_' + cleaningZoneRaw.id,
      },
    }
  }
}

const normalizeCleaningZoneEndingPoints = (cleaningZonesRaw: CleaningZoneRead[]): ICleaningZonePoint[] => {
  return cleaningZonesRaw
    .map(cleaningZone => normalizeCleaningZoneEndingPoint(cleaningZone))
    .filter(Boolean) as ICleaningZonePoint[]
}

const normalizeCleaningZoneEndingPoint = (
  cleaningZoneRaw: CleaningZoneRead | CleaningZone
): ICleaningZonePoint | undefined => {
  if (cleaningZoneRaw.ending_point?.coordinates) {
    return {
      id: 'cleaning_' + cleaningZoneRaw.id + '_ending_point',
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: cleaningZoneRaw.ending_point?.coordinates,
      },
      properties: {
        zone_id: cleaningZoneRaw.id,
        id_on_map: 'cleaning_' + cleaningZoneRaw.id,
      },
    }
  }
}

const normalizeMissionInfo = (missionRaw: MissionRead | MissionReadList): IMissionInfo => {
  return {
    id: toMissionId(missionRaw.id),
    name: missionRaw.name || '',
    apriorMapVersionId: missionRaw.aprior_map_version_id
      ? toApriorMapVersionId(missionRaw.aprior_map_version_id)
      : null,
    description: missionRaw.description || '',
    createdAt: missionRaw.created_at,
    updatedAt: missionRaw.updated_at,
    status: missionRaw.status as MISSION_CALCULATING_STATUSES,
    actualCoverage: missionRaw.coverage_fact,
    plannedCoverage: missionRaw.coverage_plan,
    timeLeft: missionRaw.execution_time,
    missionType: missionRaw.mission_type,
    cleaningArea: (missionRaw as MissionRead).cleaning_area || null,
  }
}

const normalizePoint = (
  pointRaw?: { type?: 'Point'; coordinates?: Array<number> } | null,
  start: boolean = true
): IPoint | undefined => {
  if (pointRaw && pointRaw.coordinates) {
    const id = start ? 'mission_start_point' : 'mission_finish_point'
    return {
      id,
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: pointRaw?.coordinates,
      },
      properties: {
        id_on_map: id,
      },
    }
  }
}

const normalizeMission = (missionRaw: MissionRead): IMission => {
  const mission = normalizeMissionInfo(missionRaw)
  const status = missionRaw.status
    ? (missionRaw.status as MISSION_CALCULATING_STATUSES)
    : MISSION_CALCULATING_STATUSES.NEW
  const isReadyForCalculation = missionRaw.is_ready_to_start_calculation

  const cleaningZones = missionRaw.cleaning_zones
    .map(zoneRaw => normalizeCleaningZone(zoneRaw))
    .sort((a, b) => (a.properties.sorting ?? 0) - (b.properties.sorting ?? 0))
  const restrictZones = missionRaw.restrict_zones.map(zoneRaw => normalizeRestrictZone(zoneRaw))
  const cleaningRoutes = missionRaw.cleaning_zones
    .filter(zoneRaw => zoneRaw.cleaning_route_geometry)
    .map(zoneRaw => normalizeCleaningRoute(zoneRaw))
  const transferRoutes = missionRaw.transfer_routes.map(transferRouteRaw => normalizeTransferRoute(transferRouteRaw))
  const cleaningZoneStartingPoints = normalizeCleaningZoneStartingPoints(missionRaw.cleaning_zones)
  const cleaningZoneEndingPoints = normalizeCleaningZoneEndingPoints(missionRaw.cleaning_zones)
  const startingPoint = normalizePoint(missionRaw.starting_point, true)
  const finishingPoint = normalizePoint(missionRaw.finishing_point, false)
  const pointRoutes = missionRaw.points_transfer_routes.map(routeRaw => normalizeRoute(routeRaw))
  const visualizationSettings = { ...DEFAULT_VIZUALIZATION_SETTINGS, ...missionRaw.visualization_settings }
  return {
    status,
    mission,
    cleaningZones,
    restrictZones,
    cleaningRoutes,
    cleaningZoneStartingPoints,
    cleaningZoneEndingPoints,
    isReadyForCalculation,
    transferRoutes,
    startingPoint,
    finishingPoint,
    pointRoutes,
    visualizationSettings,
  }
}

let statusIsPending = false

export const MainApi = {
  fetchMissions: async (pageSize?: number): Promise<IMissionInfo[]> => {
    const missions = await MainService.mainMissionList(undefined, undefined, pageSize)
    if (!missions.results) {
      return []
    }
    const normalizedMissions = missions.results.map(missionRaw => normalizeMissionInfo(missionRaw))
    return normalizedMissions
  },
  createMission: async (): Promise<Mission> => {
    const mission = await MainService.mainMissionCreate({
      name: 'New mission',
      description: '',
    })
    return mission
  },
  fetchMission: async (id: number): Promise<IMission> => {
    const missionRaw = await MainService.mainMissionRetrieve(id)
    return normalizeMission(missionRaw)
  },
  changeMissionName: async (id: number, name: string): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      name,
    })
    return missionRaw
  },
  changeMissionApriorMapVersion: async (id: number, aprior_map_version_id: number | null): Promise<MissionRead> => {
    const payload: PatchedMissionRequest = { aprior_map_version_id }
    const missionRaw = await MainService.mainMissionPartialUpdate(id, payload)
    return missionRaw as MissionRead
  },
  changeMissionVisualSettings: async (id: number, visualSettings: IVisualizationSettings): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      visualization_settings: visualSettings,
    })
    return missionRaw
  },
  changeMissionStartPoint: async (data: PropsPoints): Promise<IPoint | undefined> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(data.id, {
      starting_point: data.startingPoint,
    })
    return normalizePoint(missionRaw.starting_point)
  },
  changeMissionFinishPoint: async (data: PropsPoints): Promise<IPoint | undefined> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(data.id, {
      finishing_point: data.finishingPoint,
    })
    return normalizePoint(missionRaw.finishing_point)
  },
  deleteMission: async (id: number): Promise<void> => {
    await MainService.mainMissionDestroy(id)
  },
  createRestrictZone: async (data: ZoneCreateData): Promise<IZone> => {
    const zone = await MainService.mainRestrictZoneCreate({
      name: data.name,
      description: data.description,
      mission_id: data.missionId,
      geometry: data.geometry,
    })
    return normalizeRestrictZone(zone)
  },
  deleteRestrictZone: async (id: number): Promise<void> => {
    await MainService.mainRestrictZoneDestroy(id)
  },
  patchRestrictZone: async (data: ZoneUpdateData): Promise<IZone> => {
    const { zoneId } = data
    const zone = await MainService.mainRestrictZonePartialUpdate(zoneId, {
      name: data.name,
      description: data.description,
      mission_id: data.missionId,
      geometry: data.geometry,
    })
    return normalizeRestrictZone(zone)
  },
  createCleaningZone: async (
    data: ZoneCreateData
  ): Promise<{
    zone: ICleaningZone
    startingPoint: ICleaningZonePoint | undefined
    endingPoint: ICleaningZonePoint | undefined
  }> => {
    const zone = await MainService.mainCleaningZoneCreate({
      name: data.name,
      description: data.description,
      mission_id: data.missionId,
      geometry: data.geometry,
      starting_point: data.startingPoint ?? null,
      ending_point: data.endingPoint ?? null,
      cleaning_config: data.cleaningConfig ? toDtoCleaningConfig(data.cleaningConfig) : null,
    })
    return {
      zone: normalizeCleaningZone(zone),
      startingPoint: normalizeCleaningZoneStartingPoint(zone),
      endingPoint: normalizeCleaningZoneEndingPoint(zone),
    }
  },
  patchCleaningZone: async (
    data: ZoneUpdateData
  ): Promise<{
    zone: IZone
    startingPoint: ICleaningZonePoint | undefined
    endingPoint: ICleaningZonePoint | undefined
  }> => {
    const { zoneId } = data

    const zone = await MainService.mainCleaningZonePartialUpdate(zoneId, {
      name: data.name,
      description: data.description,
      mission_id: data.missionId,
      geometry: data.geometry,
      starting_point: data.startingPoint ?? null,
      ending_point: data.endingPoint ?? null,
      ...(data.cleaningConfig !== undefined && { cleaning_config: toDtoCleaningConfig(data.cleaningConfig) }),
    })

    return {
      zone: normalizeCleaningZone(zone),
      startingPoint: normalizeCleaningZoneStartingPoint(zone),
      endingPoint: normalizeCleaningZoneEndingPoint(zone),
    }
  },
  fetchCleaningZone: async (zoneId: number) => {
    const zone = await MainService.mainCleaningZoneRetrieve(zoneId)
    return { zone: normalizeCleaningZone(zone), route: normalizeCleaningRoute(zone) }
  },
  deleteCleaningZone: async (id: number): Promise<void> => {
    await MainService.mainCleaningZoneDestroy(id)
  },
  reorderCleaningZones: async (data: Array<CleaningZoneOrderingRequest>): Promise<IMission> => {
    const missionRaw = await MainService.mainCleaningZoneOrderingPartialUpdate(data)
    return normalizeMission(missionRaw)
  },
  startCalculatingRoutes: async (missionId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainMissionStartCalculationCreate(missionId)
  },
  fetchCalculationStatus: async (missionId: number): Promise<MissionStatus | undefined> => {
    if (statusIsPending) {
      return
    }
    statusIsPending = true
    const status = await MainService.mainMissionGetStatusRetrieve(missionId)
    if (!statusIsPending) {
      return
    }
    statusIsPending = false
    return status
  },
  patchTransferRoute: async (id: number, geometry?: LineString): Promise<void> => {
    await MainService.mainTransferRoutePartialUpdate(id, {
      user_geometry: geometry,
    })
  },
  deleteTransferRoute: async (id: number): Promise<void> => {
    await MainService.mainTransferRouteDestroy(id)
  },
  patchMissionPointTransferRoute: async (id: number, geometry?: LineString): Promise<void> => {
    await MainService.mainMissionPointTransferRoutePartialUpdate(id, {
      user_geometry: geometry,
    })
  },
  deleteMissionPointTransferRoute: async (id: number): Promise<void> => {
    await MainService.mainMissionPointTransferRouteDestroy(id)
  },
}
